import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  RegisterDeviceData,
  RegisterDeviceRequest,
  RegisterDeviceResponse,
} from 'generated/openapi';
import { toast } from 'sonner';

import { logger } from '@/features/logging/service/logger';
import { useSetFcmToken } from '@/features/notifications/atoms/fcmToken';
import { refreshNotificationPermission } from '@/features/notifications/atoms/notificationPermission';
import {
  NotificationBrowserBlockedError,
  NotificationError,
  NotificationPermissionDeniedError,
  NotificationPushServiceError,
  NotificationTokenTimeoutError,
  NotificationUnsupportedError,
} from '@/features/notifications/errors';
import { getPushToken } from '@/features/notifications/utils/pushNotifications';
import { getBalanceQueryKey } from '@/features/subscription/hooks/useGetBalance/getBalance.options';
import { PrivateApi } from '@/shared/api/httpClient';

const registerDeviceUrl: RegisterDeviceData['url'] = 'users/@me/devices';

export type NotificationOptInSource =
  | 'onboarding'
  | 'user_settings'
  | 'token_refresh'
  | 'reminder_modal_new_session'
  | 'reminder_modal_swipes'
  | 'reminder_modal_comms_page'
  | 'reminder_modal_duos_page'
  | 'play_now_modal'
  | 'swipe_balance_modal'
  | 'reveal_balance_modal'
  | 'comms_banner';

export interface RegisterDeviceParams {
  platform: RegisterDeviceRequest['platform'];
  source: NotificationOptInSource;
  /** Optional pre-fetched token. If not provided, fetches a new token. */
  token?: string;
  /**
   * If true, suppresses all toasts (success and error).
   * Use for background token refresh where user feedback is not needed.
   */
  silent?: boolean;
}

const registerDevice = async ({
  platform,
  source,
  token: providedToken,
}: Omit<RegisterDeviceParams, 'silent'>) => {
  const token = providedToken ?? (await getPushToken(platform));

  // Refresh permission state (for UI consistency)
  await refreshNotificationPermission();

  return PrivateApi.post(registerDeviceUrl, {
    json: {
      platform,
      token,
    },
    headers: {
      'Duos-Origin': source,
    },
  }).json<RegisterDeviceResponse>();
};

function handleNotificationError(error: unknown, silent: boolean): void {
  if (silent) return;

  if (error instanceof NotificationBrowserBlockedError) {
    logger.error('Notification: browser blocked', error);
    toast.error('Notifications blocked by browser', {
      description: 'Allow notifications in browser settings and retry.',
    });
    return;
  }

  if (error instanceof NotificationPermissionDeniedError) {
    if (error.platform === 'IOS') {
      // iOS permission denial is often temporary/soft - don't alarm the user
      logger.error('Notification: permission denied iOS', error);
      toast.warning('Notifications permission was not granted');
      return;
    }

    if (error.platform === 'ANDROID') {
      logger.error('Notification: permission denied Android', error);
      toast.error('Notifications permission denied', {
        description: 'Enable notifications in app settings to receive alerts.',
      });
      return;
    }

    // WEB - already handled by NotificationBrowserBlockedError for blocked state
    logger.error('Notification: permission denied Web', error);
    toast.warning('Notifications permission was not granted');
    return;
  }

  if (error instanceof NotificationTokenTimeoutError) {
    logger.error('Notification: token timeout', error);
    toast.error('Notification setup timed out', {
      description: 'Please check your connection and try again.',
    });
    return;
  }

  if (error instanceof NotificationUnsupportedError) {
    logger.error('Notification: unsupported', error);
    toast.error('Notifications not supported', {
      description:
        'Your browser or device does not support push notifications.',
    });
    return;
  }

  if (error instanceof NotificationPushServiceError) {
    logger.error('Notification: push service error', error);
    toast.error('Push service unavailable', {
      description:
        'Your browser failed to connect to the push service. Try a different browser or retry later.',
    });
    return;
  }

  // Generic notification error or unknown error
  if (error instanceof NotificationError) {
    logger.error('Notification: generic error', error);
    toast.error('Failed to enable notifications', {
      description: error.message,
    });
    return;
  }

  // Network or API error
  logger.error('Notification: unknown error', error);
  toast.error('Failed to enable notifications', {
    description: 'Something went wrong. Please try again.',
  });
}

export function useRegisterDevice() {
  const queryClient = useQueryClient();
  const setFcmToken = useSetFcmToken();

  return useMutation({
    mutationFn: ({ silent: _, ...params }: RegisterDeviceParams) =>
      registerDevice(params),
    onSuccess: (response, { silent }) => {
      setFcmToken(response.device.token);
      void queryClient.invalidateQueries({
        queryKey: getBalanceQueryKey(),
      });

      if (!silent) {
        toast.success('Notifications enabled');
      }
    },
    onError: (error, { silent }) => {
      handleNotificationError(error, silent ?? false);
    },
  });
}
